import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Flex, Form, Input, InputNumber, Select, Typography, message } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { invalidateAfterRefund } from '../../components/refund-api';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import { listStockLocations } from '../parts/counts-api';
import { issueCreditNote, type CreditLineInput, type Invoice, type InvoiceLine } from './api';
import { creditPreview, lineCreditAmount, type PreviewLine } from './credit-preview';
import { invalidateAfterInvoiceMoney } from './invalidate-invoice-money';

const { Text } = Typography;

export interface CreditLinesDrawerProps {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
}

/** A decimal string minus another, in thousandths, as a decimal string. Both are non-negative. */
function remainingQty(line: InvoiceLine): string {
  const toMilli = (v: string): bigint => {
    const [w = '0', f = ''] = v.split('.');
    return BigInt(`${w}${f.padEnd(3, '0').slice(0, 3)}`);
  };
  const left = toMilli(line.qty) - toMilli(line.creditedQty ?? '0');
  const digits = (left < 0n ? 0n : left).toString().padStart(4, '0');
  return `${digits.slice(0, -3)}.${digits.slice(-3)}`;
}

/**
 * Credits part of an invoice, line by line. The sums shown here only preview what
 * the server will work out (credit-preview.ts); the server stays authoritative.
 */
export function CreditLinesDrawer({ invoice, open, onClose }: CreditLinesDrawerProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const counter = invoice.source === 'COUNTER';

  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [shelf, setShelf] = useState<Record<string, boolean>>({});
  const [locations, setLocations] = useState<Record<string, string | undefined>>({});
  const [reason, setReason] = useState('');
  // One key per drawer open: a retry of the same submit replays, a new open is a new credit.
  const key = useRef('');
  // The lines as they stand, read when the drawer opens. A refetch while it is open must not
  // wipe what the cashier typed or spend a new key, so the reset below does not depend on them.
  const latestLines = useRef(invoice.lines);
  latestLines.current = invoice.lines;

  useEffect(() => {
    if (!open) return;
    key.current = crypto.randomUUID();
    setQtys({});
    setReason('');
    const defaults: Record<string, boolean> = {};
    const where: Record<string, string | undefined> = {};
    for (const line of latestLines.current) {
      if (line.isPart === true) defaults[line.uid] = counter;
      where[line.uid] = line.issuedFromLocationUid ?? undefined;
    }
    setShelf(defaults);
    setLocations(where);
  }, [open, invoice.uid, counter]);

  const { data: stockLocations = [] } = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listStockLocations,
    enabled: open && !counter,
  });

  const shelfAble = (line: InvoiceLine): boolean => line.isPart === true && line.hasSku === true;
  const picked = (line: InvoiceLine): boolean => Number(qtys[line.uid] ?? '0') > 0;
  const toShelf = (line: InvoiceLine): boolean => shelfAble(line) && shelf[line.uid] === true;

  const previewLines: PreviewLine[] = invoice.lines.map((line) => ({
    qty: line.qty,
    creditedQty: line.creditedQty ?? '0',
    creditedAmount: line.creditedAmount ?? '0',
    netTotal: line.netTotal ?? line.lineTotal,
    creditNow: qtys[line.uid] ?? '0',
    backToStock: toShelf(line),
  }));
  const preview = creditPreview(previewLines, {
    grandTotal: invoice.grandTotal,
    creditedTotal: invoice.creditedTotal ?? '0',
    paidTotal: invoice.paidTotal,
  });

  const chosen = invoice.lines.filter(picked);
  const reasonOk = reason.trim().length >= 3 && reason.trim().length <= 255;
  const missingLocation = chosen.some((l) => !counter && toShelf(l) && (locations[l.uid] ?? '') === '');
  const canSubmit = chosen.length > 0 && reasonOk && !missingLocation;

  const credit = useMutation({
    mutationFn: () => {
      const lines: CreditLineInput[] = chosen.map((line) => {
        const out: CreditLineInput = { invoiceLineUid: line.uid, qty: qtys[line.uid] ?? '0' };
        if (shelfAble(line)) {
          out.backToStock = toShelf(line);
          const where = locations[line.uid];
          if (!counter && toShelf(line) && where !== undefined) out.locationUid = where;
        }
        return out;
      });
      return issueCreditNote(invoice.uid, { reason: reason.trim(), lines }, key.current);
    },
    onSuccess: (updated) => {
      invalidateAfterInvoiceMoney(queryClient, { uid: invoice.uid, bookingUid: invoice.bookingUid });
      // The part credit becomes the customer's refundable credit.
      invalidateAfterRefund(queryClient);
      const note = updated.creditNotes?.at(-1);
      void message.success(t('pos.creditLines.done', { no: note?.creditNoteNo ?? '' }));
      onClose();
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('pos.creditLines.failed'));
    },
  });

  return (
    <ActionDrawer
      open={open}
      title={t('pos.creditLines.title')}
      submitLabel={t('pos.creditLines.submit', { amount: formatMoney(preview.credit) })}
      submitting={credit.isPending}
      submitDisabled={!canSubmit}
      onClose={onClose}
      onSubmit={() => {
        credit.mutate();
      }}
      consequence={t('pos.creditLines.consequence', {
        credit: formatMoney(preview.credit),
        total: formatMoney(preview.newTotal),
        beyond: formatMoney(preview.beyondTotal),
        parts: t('pos.creditLines.parts', { count: Number(preview.partsBack) }),
      })}
    >
      <Flex vertical gap={16}>
        {invoice.lines.map((line, index) => {
          const left = remainingQty(line);
          const exhausted = Number(left) <= 0;
          return (
            <Flex
              key={line.uid}
              vertical
              gap={8}
              data-testid={`credit-line-${line.uid}`}
              style={{ paddingBottom: 12, borderBottom: '1px solid rgba(128,128,128,0.2)' }}
            >
              <Text strong>{line.description}</Text>
              <Flex gap={16} wrap>
                <Text type="secondary">
                  {t('pos.creditLines.invoiced')}: {line.qty}
                </Text>
                <Text type="secondary">
                  {t('pos.creditLines.alreadyCredited')}: {line.creditedQty ?? '0'}
                </Text>
              </Flex>
              {exhausted ? (
                <Text type="secondary">{t('pos.creditLines.nothingLeft')}</Text>
              ) : (
                <Flex vertical gap={8}>
                  <Flex align="center" gap={8} wrap>
                    <label htmlFor={`credit-qty-${line.uid}`}>{t('pos.creditLines.creditNow')}</label>
                    <InputNumber
                      id={`credit-qty-${line.uid}`}
                      aria-label={`${t('pos.creditLines.creditNow')} ${line.description}`}
                      stringMode
                      min="0"
                      max={left}
                      step="1"
                      precision={3}
                      value={qtys[line.uid] ?? '0'}
                      size="large"
                      onChange={(v) => {
                        setQtys((prev) => ({ ...prev, [line.uid]: v ?? '0' }));
                      }}
                    />
                    {picked(line) && (
                      <Text type="secondary">{formatMoney(lineCreditAmount(previewLines[index] as PreviewLine))}</Text>
                    )}
                  </Flex>
                  {shelfAble(line) && (
                    <Checkbox
                      checked={shelf[line.uid] === true}
                      onChange={(e) => {
                        setShelf((prev) => ({ ...prev, [line.uid]: e.target.checked }));
                      }}
                    >
                      {t('pos.creditLines.toShelf')}
                    </Checkbox>
                  )}
                  {!counter && toShelf(line) && (
                    <Select
                      aria-label={t('pos.creditLines.location')}
                      placeholder={t('pos.creditLines.locationPlaceholder')}
                      value={locations[line.uid]}
                      size="large"
                      style={{ width: '100%' }}
                      options={stockLocations.map((l) => ({ value: l.uid, label: l.name }))}
                      onChange={(v: string) => {
                        setLocations((prev) => ({ ...prev, [line.uid]: v }));
                      }}
                    />
                  )}
                </Flex>
              )}
            </Flex>
          );
        })}
        <Form layout="vertical">
          <Form.Item label={t('pos.creditLines.reason')} htmlFor="credit-reason" required>
            <Input.TextArea
              id="credit-reason"
              rows={3}
              maxLength={255}
              value={reason}
              placeholder={t('pos.creditLines.reasonPlaceholder')}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </Flex>
    </ActionDrawer>
  );
}
